import { HttpErrorResponse } from '@angular/common/http';
import { ErrorHandler } from '@angular/core';
import { interpretError } from '@app/react/errors/useInterpretError';
import * as Sentry from '@sentry/angular';
import { isAxiosError } from 'axios';
import { ApplicationConfig } from '../_models/applicationConfigDto';
import { RequestCancelledError } from '../_providers/requestCancelledError';

/**
 * Error rebuilt from an HttpErrorResponse that Sentry's default extractor would have reduced to a
 * bare message string -> <unknown>
 */
class HttpResponseError extends Error {
  readonly context: Record<string, unknown>;

  constructor(message: string, response: HttpErrorResponse) {
    super(message);
    this.stack = undefined;
    this.context = {
      status: response.status,
      statusText: response.statusText,
      url: response.url,
      responseBody: describeResponseBody(response.error),
    };
  }
}

function describeResponseBody(body: unknown): string {
  const MAX_BODY_SNIPPET_LENGTH = 500;
  const value = body != null && typeof body === 'object' && 'text' in body ? body.text : body;

  try {
    return String(typeof value === 'string' ? value : JSON.stringify(value)).slice(0, MAX_BODY_SNIPPET_LENGTH);
  } catch {
    return String(value).slice(0, MAX_BODY_SNIPPET_LENGTH);
  }
}

/**
 * Wraps Sentry's default extractor to handle two fallback cases:
 * - HttpErrorResponses reduced to a message string (status/body lost — restored via HttpResponseError)
 * - null results (plain objects, undefined, null, etc.) — which otherwise become `<unknown>` in Sentry
 */
function extractError(error: unknown, defaultExtractor: (error: unknown) => unknown): unknown {
  const result = defaultExtractor(error);

  const unwrapped =
    error != null && typeof error === 'object' && 'ngOriginalError' in error ? error.ngOriginalError : error;
  if (unwrapped instanceof HttpErrorResponse && !(result instanceof Error)) {
    return new HttpResponseError(typeof result === 'string' ? result : unwrapped.message, unwrapped);
  }

  if (result != null) {
    return result;
  }

  // Default extractor returned null — build a meaningful error from whatever we got
  if (error == null) {
    return new Error(`Non-Error exception captured: ${String(error)}`);
  }

  try {
    return new Error(`Non-Error exception captured: ${JSON.stringify(error)}`);
  } catch {
    return new Error(`Non-Error exception captured: ${String(error)}`);
  }
}

export const sentryProviders = [
  {
    provide: ErrorHandler,
    useValue: Sentry.createErrorHandler({
      showDialog: false,
      extractor: extractError,
    }),
  },
];

export function initSentry(config: ApplicationConfig): void {
  if (!config.sentryDsn) {
    return;
  }

  Sentry.init({
    dsn: config.sentryDsn,
    environment: config.sentryEnvironment,
    release: config.commitSha ?? undefined,
    tracesSampleRate: config.sentryTracesSampleRate ?? 0,
    integrations: [
      Sentry.browserTracingIntegration(),
      ...(config.sentryEnvironment === 'development' ? [Sentry.spotlightBrowserIntegration()] : []),
    ],
    tracePropagationTargets: [new RegExp(`^https://${globalThis.location.hostname}`)],
    ignoreErrors: [
      '401 Unauthorized',
      '403 Forbidden', // can happen if user is locked (e.g., after registering)
      '403 OK', // not sure why we get 'OK' sometimes, but the reason is the same as above
      '401 OK',
      '502 Bad Gateway', // can happen during deployments
      '503 Service Unavailable', // can happen during deployments
      '0 Unknown Error', // happens in some browsers (Firefox, Safari) if requests get cancelled
      'RequestCancelledError', // requests cancelled by the browser (status 0), e.g. during logout
      'Unexpected server response (401) while retrieving PDF', // uncaught but handled 401 in PDF.js
      /startCleanup: Page \d+ is currently rendering/, // https://github.com/stephanrauh/ngx-extended-pdf-viewer/issues/645
      // failed chunk -> only reported if reload didn't help
      /dynamically imported module/, // Chrome, Firefox
      'Importing a module script failed', // Safari
    ],
    beforeSend(event, hint) {
      const error = hint?.originalException;

      // Cancelled requests (status 0) are expected during logout/navigation and not actionable
      if (error instanceof RequestCancelledError) {
        return null;
      }

      // Expected React API errors (session expired, forbidden, backend offline) — the React ErrorBoundary reports them anyway
      if (isAxiosError(error) && !interpretError(error).shouldBeReported) {
        return null;
      }

      // Drop events that still have no useful information after extraction
      if (event.exception?.values?.every((v) => !v.value || v.value === 'undefined' || v.value === 'null')) {
        return null;
      }

      // Add the current URL as a tag for easier filtering
      event.tags = { ...event.tags, url: globalThis.location.href };

      // Attach the HTTP details (status, url, body snippet) preserved by the extractor
      if (error instanceof HttpResponseError) {
        event.extra = { ...event.extra, ...error.context };
      }

      // If the original exception was not a proper Error, tag it
      if (error != null && !(error instanceof Error)) {
        event.tags['non_error_exception'] = typeof error;
        event.extra = {
          ...event.extra,
          originalError: typeof error === 'object' ? JSON.stringify(error) : String(error),
        };
      }

      return event;
    },
  });
}
